/*
 * Personal Digital Spaces LLC ("COMPANY") CONFIDENTIAL
 * Unpublished Copyright (c) 2025 Personal Digital Spaces LLC, All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of COMPANY. The intellectual and technical concepts contained
 * herein are proprietary to COMPANY and may be covered by U.S. and Foreign Patents, patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from COMPANY.  Access to the source code contained herein is hereby forbidden to anyone except current COMPANY employees, managers, contractors or partners who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of  COMPANY.   ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */
import { useEffect, useRef, useCallback, useState } from 'react'
import { useAuth0 } from '@auth0/auth0-react'

export const useAutoCompleteInputText = ({
  currentFilterId,
  filterValue,
  filters,
  keyData,
  keyLabel,
  keyValue,
  minSearchLength
}) => {
  const { getAccessTokenSilently: getAccessToken } = useAuth0()
  const debounceTimerRef = useRef(null)
  const [value, setValue] = useState(
    filterValue ? { value: filterValue, label: filterValue } : null
  )
  const debounce = (fn, delay) => {
    return function (...args) {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
      debounceTimerRef.current = setTimeout(() => {
        fn(...args)
      }, delay)
    }
  }

  const performSearch = useCallback(async (inputValue) => {
    if (!inputValue || inputValue.length < minSearchLength) return []

    try {
      const accessToken = await getAccessToken()

      const currentFilter = filters.find(
        filter => filter.id === currentFilterId
      )

      if (!currentFilter || !currentFilter.api) {
        console.error('Filter not found or API not defined')
        return []
      }

      const response = await fetch(
        currentFilter.api(inputValue),
        {
          method: 'GET',
          headers: {
            'content-type': 'application/json',
            Authorization: `Bearer ${accessToken}`
          }
        }
      )

      if (!response.ok) {
        throw new Error('Error on API')
      }

      const data = await response.json()

      if (!data || !Array.isArray(data[keyData])) {
        console.warn(
          `Invalid data structure received. Expected array at key "${keyData}"`
        )
        return []
      }

      const options = data[keyData].map(option => ({
        value: option[keyValue],
        label: option[keyLabel]
      }))

      return options
    } catch (error) {
      console.error('Error:', error)

      return []
    }
  }, [currentFilterId, filters])

  const debouncedSearch = useCallback(
    debounce((inputValue, callback) => {
      performSearch(inputValue).then(options => callback(options))
    }, 500),
    [performSearch]
  )

  const loadOptions = (inputValue, callback) => {
    if (!inputValue || inputValue.length < minSearchLength) {
      callback([])
      return
    }

    debouncedSearch(inputValue, callback)
  }

  const fetchLabelById = useCallback(async (id) => {
    if (!id) return null

    try {
      const currentFilter = filters.find(
        filter => filter.id === currentFilterId
      )

      if (!currentFilter || !currentFilter.apiById) {
        return null
      }

      const accessToken = await getAccessToken()
      const response = await fetch(
        currentFilter.apiById(id),
        {
          method: 'GET',
          headers: {
            'content-type': 'application/json',
            Authorization: `Bearer ${accessToken}`
          }
        }
      )

      if (!response.ok) {
        throw new Error('Error fetching item by ID')
      }

      const apiResponse = await response.json()

      if (!apiResponse) {
        return null
      }

      const data = keyData
        ? apiResponse[keyData]
        : apiResponse

      const item = currentFilter.singleResultIsObject
        ? data
        : data?.[0]

      if (!item) {
        return null
      }

      return {
        value: item[keyValue],
        label: item[keyLabel]
      }
    } catch (error) {
      console.error('Error fetching label by ID:', error)
      return null
    }
  }, [currentFilterId, filters, getAccessToken, keyData, keyLabel, keyValue])

  useEffect(() => {
    const loadInitialValue = async () => {
      if (filterValue) {
        const result = await fetchLabelById(filterValue)
        if (result) {
          setValue(result)
        } else {
          setValue({ value: filterValue, label: filterValue })
        }
      } else {
        setValue(null)
      }
    }

    loadInitialValue()
  }, [filterValue, fetchLabelById])

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
    }
  }, [])

  return {
    value,
    loadOptions
  }
}
